import { supabase } from "@/integrations/supabase/client";

interface Breadcrumb {
  timestamp: number;
  category: 'navigation' | 'fetch' | 'ui' | 'error' | 'console';
  message: string;
  data?: Record<string, any>;
}

interface ErrorReport {
  message: string;
  stack?: string;
  component_stack?: string;
  severity?: 'fatal' | 'error' | 'warn' | 'info';
  is_unhandled_rejection?: boolean;
  route?: string;
  url?: string;
  metadata?: Record<string, any>;
}

interface ErrorReporterConfig {
  release?: string;
  environment?: string;
  maxBreadcrumbs?: number;
  maxReportsPerMinute?: number;
}

class ErrorReporter {
  private breadcrumbs: Breadcrumb[] = [];
  private config: Required<ErrorReporterConfig>;
  private sessionId: string;
  private reportQueue: ErrorReport[] = [];
  private reportTimestamps: number[] = [];
  private fingerprints: Set<string> = new Set();

  constructor(config: ErrorReporterConfig = {}) {
    this.config = {
      release: config.release || this.getVersion(),
      environment: config.environment || 'production',
      maxBreadcrumbs: config.maxBreadcrumbs || 50,
      maxReportsPerMinute: config.maxReportsPerMinute || 5,
    };
    this.sessionId = this.generateSessionId();
    this.setupGlobalHandlers();
    this.patchFetch();
  }

  private getVersion(): string {
    return import.meta.env.VITE_APP_VERSION || new Date().toISOString().split('T')[0];
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private setupGlobalHandlers(): void {
    // Catch unhandled errors
    window.addEventListener('error', (event) => {
      this.report({
        message: event.message,
        stack: event.error?.stack,
        severity: 'error',
        url: event.filename,
        metadata: {
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    });

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.report({
        message: event.reason?.message || String(event.reason),
        stack: event.reason?.stack,
        severity: 'error',
        is_unhandled_rejection: true,
      });
    });
  }

  private patchFetch(): void {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const startTime = Date.now();
      const url = typeof args[0] === 'string' ? args[0] : (args[0] instanceof Request ? args[0].url : String(args[0]));
      const method = args[1]?.method || 'GET';

      this.addBreadcrumb({
        category: 'fetch',
        message: `${method} ${url}`,
        data: { method, url },
      });

      try {
        const response = await originalFetch(...args);
        const duration = Date.now() - startTime;

        this.addBreadcrumb({
          category: 'fetch',
          message: `${method} ${url} - ${response.status}`,
          data: { method, url, status: response.status, duration },
        });

        return response;
      } catch (error) {
        const duration = Date.now() - startTime;
        this.addBreadcrumb({
          category: 'fetch',
          message: `${method} ${url} - Failed`,
          data: { method, url, error: String(error), duration },
        });
        throw error;
      }
    };
  }

  addBreadcrumb(breadcrumb: Omit<Breadcrumb, 'timestamp'>): void {
    this.breadcrumbs.push({
      ...breadcrumb,
      timestamp: Date.now(),
    });

    // Keep only last N breadcrumbs
    if (this.breadcrumbs.length > this.config.maxBreadcrumbs) {
      this.breadcrumbs.shift();
    }
  }

  private generateFingerprint(message: string, stack?: string): string {
    const stackLines = stack?.split('\n').slice(0, 3).join('') || '';
    return `${message}:${stackLines}`;
  }

  private isRateLimited(): boolean {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;
    
    // Remove old timestamps
    this.reportTimestamps = this.reportTimestamps.filter(ts => ts > oneMinuteAgo);
    
    return this.reportTimestamps.length >= this.config.maxReportsPerMinute;
  }

  async report(error: ErrorReport): Promise<string | null> {
    try {
      const fingerprint = this.generateFingerprint(error.message, error.stack);

      // Check for duplicate in current session
      if (this.fingerprints.has(fingerprint)) {
        console.warn('[ErrorReporter] Duplicate error, skipping:', error.message);
        return null;
      }

      // Check rate limit
      if (this.isRateLimited()) {
        console.warn('[ErrorReporter] Rate limit exceeded, queuing error');
        this.reportQueue.push(error);
        return null;
      }

      this.fingerprints.add(fingerprint);
      this.reportTimestamps.push(Date.now());

      const payload = {
        ...error,
        route: window.location.pathname,
        url: window.location.href,
        release: this.config.release,
        environment: this.config.environment,
        session_id: this.sessionId,
        user_agent: navigator.userAgent,
        device: this.getDeviceInfo(),
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        fingerprint,
        breadcrumbs: this.breadcrumbs,
        metadata: {
          ...error.metadata,
          timestamp: new Date().toISOString(),
        },
      };

      // Use sendBeacon if available for reliability
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const endpoint = `${supabaseUrl}/functions/v1/report-error`;

      if (navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(endpoint, blob);
        return fingerprint;
      } else {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          const result = await response.json();
          return result.id;
        }
      }

      return fingerprint;
    } catch (err) {
      console.error('[ErrorReporter] Failed to report error:', err);
      return null;
    }
  }

  private getDeviceInfo(): string {
    const ua = navigator.userAgent;
    if (/mobile/i.test(ua)) return 'mobile';
    if (/tablet/i.test(ua)) return 'tablet';
    return 'desktop';
  }

  // Public method for manual error reporting
  captureException(error: Error, metadata?: Record<string, any>): Promise<string | null> {
    return this.report({
      message: error.message,
      stack: error.stack,
      severity: 'error',
      metadata,
    });
  }

  // Public method for manual messages
  captureMessage(message: string, severity: 'info' | 'warn' | 'error' = 'info'): Promise<string | null> {
    return this.report({
      message,
      severity,
    });
  }
}

// Singleton instance
let reporterInstance: ErrorReporter | null = null;

export function initErrorReporter(config?: ErrorReporterConfig): ErrorReporter {
  if (!reporterInstance) {
    reporterInstance = new ErrorReporter(config);
  }
  return reporterInstance;
}

export function getErrorReporter(): ErrorReporter {
  if (!reporterInstance) {
    throw new Error('ErrorReporter not initialized. Call initErrorReporter() first.');
  }
  return reporterInstance;
}

export { ErrorReporter };
export type { ErrorReport, Breadcrumb, ErrorReporterConfig };
